import { Button, Grid, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import BlockData from '../../../models/BlockData';
import BlockProps from '../../../models/BlockProps';
import Utils from '../../../utils/Utils';
import BlockImageListData from './BlockImageListData';
import CloseButton from '../../Buttons/Close';
import GridWithSettings from '../../Layout/GridWithSettings';
import BlockImageListItemData from './BlockImageListItemData';
import BlockImageListItem from './components/BlockImageListItem';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import config from '../../../config';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

export interface BlockImageListProps extends BlockProps {
  blockData: BlockData[]
}

function BlockImageList({
  data,
  isEditing,
  isTemplate,
  editHandler,
  blockData,
  showImageModal,
  onCloseImageModal,
}: BlockImageListProps) {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockImageListData>(data.data as BlockImageListData);

  useEffect(() => {
    setProps(data.data as BlockImageListData);
  }, [data.data]);

  const handleAddImage = () => {
    props.imageList.push(BlockImageListItemData.getDefault(config.defaultImageUrl));
    editHandler(data.id, props);
  };

  const imageMenu = useMemo(() => {
    if (props.imageList && props.imageList.length > 0) {
      return props.imageList.map((item, index) => (
        <Grid
          key={item.id}
          minWidth={150}
          style={{ padding: 10 }}
          item
          xs={12}
          sm={6}
          md={3}
          sx={Utils.hoverBorder(isEditing)}
          onClick={(event) => event.stopPropagation()}
        >
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.imageList.splice(index, 1);
              editHandler(data.id, props);
            }}
          />
          <BlockImageListItem
            item={item}
            isEditing={isEditing}
            blockData={blockData}
            isTemplate={isTemplate}
            targetModalId={targetModalId}
            onUpdate={(newImage) => {
              const newItem = props.imageList[index];
              newItem.image = newImage;
              props.imageList[index] = newItem;
              editHandler(data.id, props);
            }}
          />
        </Grid>
      ));
    }
    return [];
  }, [data.id, editHandler, isEditing, isTemplate, props, blockData]);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          overflow: 'hidden',
          p: 1.5,
          ...useGetBackground(
            props.url?.solidColor,
            props.url?.value,
            props.url?.settings?.scale,
            props.url?.settings?.scaleBool,
          ),
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <BackGroundImageModal
            backgroundImage={props.url}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(url: BlockComponentImage) => {
              const tempBg = { ...props, url: { ...url } };
              setProps(tempBg);
              editHandler(data.id, tempBg);
            }}
          />
        )}

        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="block-content-wrapper"
        >
          <GridWithSettings
            settings={props.settings}
            isEditing={isEditing}
            direction="row"
            updateHandler={(settings) => {
              props.settings = settings;
              editHandler(data.id, props);
            }}
          >
            {imageMenu}
          </GridWithSettings>
        </Grid>

        {isEditing && (
          <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: 'calc(100% - 24px)' }}>
            <Button color="primary" onClick={handleAddImage}>
              <AddCircleRoundedIcon />
            </Button>
          </Stack>
        )}
      </Grid>

      <ImageEnlargeModal
        images={[...props.imageList.map((item) => item.image)]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockImageList;
